// These defaults deliberately mirror the CMS WPTable so structured-content needs no overrides.
// Other consumers should confirm they suit their table before adopting this primitive.
import { cn } from 'cn';
import * as React from 'react';

function TableContainer({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="table-container" className={cn('relative w-full', className)} {...props} />
  );
}

function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    <table
      data-slot="table"
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn(className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn(className)} {...props} />;
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return <tr data-slot="table-row" className={cn(className)} {...props} />;
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  // RiaTable's grey.100 is un-tokened (#f7f7f7; Theme/index.ts:324).
  return (
    <th
      data-slot="table-head"
      className={cn(
        'min-w-[140px] border-b border-border bg-[#f7f7f7] px-4 py-2 text-left align-top font-medium first:pl-4 md:px-2 md:py-4 [&:not(:first-child)]:border-l',
        className,
      )}
      {...props}
    />
  );
}

interface TableCellProps extends React.ComponentProps<'td'> {
  scope?: 'row';
}

function TableCell({ className, scope, ...props }: TableCellProps) {
  const cellClassName = cn(
    'min-w-[140px] border-b border-border border-b-[#e0e0e0] px-4 py-2 first:pl-4 md:py-4 [&:not(:first-child)]:border-l',
    className,
  );

  if (scope === 'row') {
    return <th data-slot="table-cell" className={cellClassName} scope="row" {...props} />;
  }

  return <td data-slot="table-cell" className={cellClassName} {...props} />;
}

export { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow };
